import { useMutation, useQueryClient } from '@tanstack/react-query';
import sortBy from 'just-sort-by';
import { type SubmitHandler, useForm } from 'react-hook-form';

import { FormErrorMessage } from '@/components/FormErrorMessage';
import { showErrorToast, showSuccessToast } from '@/components/ToastNotification';
import { Button } from '@/components/ui/button';
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from '@/components/ui/dialog';
import {
	Drawer,
	DrawerContent,
	DrawerDescription,
	DrawerFooter,
	DrawerHeader,
	DrawerTitle,
} from '@/components/ui/drawer';
import { Input } from '@/components/ui/input';
import { createDivision, updateDivisions } from '@/hooks/mutations/divisionMutations';
import { useIsBreakpoint } from '@/hooks/use-is-breakpoint';
import type { DivisionWithCourses } from '@/lib/types';

interface DivisionModalProps {
	triggerModal(): void;
	modalData: {
		isActive: boolean;
		isEditing: boolean;
		division: DivisionWithCourses | null;
	};
	allDivisions: DivisionWithCourses[] | undefined;
}

interface FormValues {
	divisionName: string;
}

const DivisionListModal = ({ modalData, triggerModal, allDivisions }: DivisionModalProps) => {
	const { isEditing, division, isActive } = modalData;
	const {
		handleSubmit,
		register,
		formState: { errors },
	} = useForm<FormValues>();
	const queryClient = useQueryClient();
	const isMobile = useIsBreakpoint('max', 768);

	const { mutate: divisionUpdateMutation, status: updateStatus } = useMutation({
		mutationFn: updateDivisions,
		onSuccess: (returnValue) => {
			showSuccessToast('Division Updated');
			queryClient.invalidateQueries({
				queryKey: ['get-settings-divisions-disciplines'],
			});
			// update query cache
			const schoolId = import.meta.env.VITE_SCHOOL_ID;
			const previousDivisionData = queryClient.getQueryData(['get-all-divisions', schoolId]) as
				| DivisionWithCourses[]
				| undefined;
			const globalSettingObj = queryClient.getQueryData(['get-settings-divisions-disciplines']) as
				| SettingsDivisionDiscipline
				| undefined;

			if (returnValue && previousDivisionData) {
				const idx = previousDivisionData.findIndex((division) => division.id === returnValue.id);
				if (idx !== -1) {
					// need to clone data in order to not mutate the cache
					const clone = [...previousDivisionData];
					clone[idx] = returnValue;
					queryClient.setQueryData(['get-all-divisions', schoolId], clone);
					if (globalSettingObj) {
						queryClient.setQueryData(['get-settings-divisions-disciplines'], {
							...globalSettingObj,
							divisions: clone,
						});
					}
				}
			}
		},
		onError: () => {
			showErrorToast('Oh no, something went wrong... Please try again.');
		},
	});

	const { mutate: divisionCreateMutation, status: createStatus } = useMutation({
		mutationFn: createDivision,
		onSuccess: (createdDivision) => {
			showSuccessToast('Division Added');

			// update query cache
			const schoolId = import.meta.env.VITE_SCHOOL_ID;
			const previousDivisionData = queryClient.getQueryData(['get-all-divisions', schoolId]) as
				| DivisionWithCourses[]
				| undefined;
			if (previousDivisionData) {
				const updatedCache = sortBy([createdDivision, ...previousDivisionData], 'name');
				queryClient.setQueryData(['get-all-divisions', schoolId], updatedCache);
			}
			queryClient.invalidateQueries({
				queryKey: ['get-settings-divisions-disciplines'],
			});
		},
		onError: () => {
			showErrorToast('Oh no, something went wrong... Please try again.');
		},
	});

	const onSubmit: SubmitHandler<FormValues> = (formData) => {
		const { divisionName } = formData;
		if (isEditing) {
			divisionUpdateMutation({
				id: division?.id ?? '',
				name: divisionName.trim(),
				previousState: (division as any) ?? null,
			});
			return;
		}
		divisionCreateMutation({
			name: divisionName.trim(),
		});
	};

	// TODO: move to higher level utils file
	const isUnique = (value: string) => {
		const isPresent = allDivisions?.some(
			(division) => division?.name?.toLowerCase() === value.toLowerCase()
		);
		return isPresent;
	};

	const isPending = [createStatus, updateStatus].includes('pending');
	const title = isEditing ? `Editing: ${division?.name}` : 'Adding New Division';

	const handleOpenChange = (open: boolean) => {
		if (!open) triggerModal();
	};

	const formContent = (
		<form onSubmit={handleSubmit(onSubmit)} className="space-y-2">
			<Input
				type="text"
				defaultValue={division?.name ?? ''}
				placeholder="Division Name"
				{...register('divisionName', {
					required: 'Division Name is required',
					validate: (value) => {
						if (value.trim() === '') {
							return 'Division Name is required';
						}
						if (isEditing) {
							return true;
						}
						if (isUnique(value)) {
							return 'This division already exists.';
						}
						return true;
					},
				})}
			/>
			<FormErrorMessage
				className="error-message"
				isShowing={!!errors?.divisionName}
				message={errors.divisionName?.message}
			/>
		</form>
	);

	const saveButton = (
		<Button
			isLoading={isPending}
			disabled={isPending}
			type="submit"
			onClick={handleSubmit(onSubmit)}
		>
			{isEditing ? 'Save' : 'Add Division'}
		</Button>
	);

	const description = isEditing
		? 'Update the name of this division.'
		: 'Enter a name for the new division.';

	if (isMobile) {
		return (
			<Drawer open={isActive} onOpenChange={handleOpenChange}>
				<DrawerContent className="max-h-[85vh]">
					<DrawerHeader>
						<DrawerTitle className="pb-2">{title}</DrawerTitle>
						<DrawerDescription className="text-base">{description}</DrawerDescription>
					</DrawerHeader>
					<div className="px-4 pb-2 flex-1 overflow-y-auto">{formContent}</div>
					<DrawerFooter>{saveButton}</DrawerFooter>
				</DrawerContent>
			</Drawer>
		);
	}

	return (
		<Dialog open={isActive} onOpenChange={handleOpenChange}>
			<DialogContent className="max-w-[500px]">
				<DialogHeader>
					<DialogTitle className="pb-2">{title}</DialogTitle>
					<DialogDescription className="text-base">{description}</DialogDescription>
				</DialogHeader>
				<div className="mb-4">{formContent}</div>
				<div className="flex justify-end">{saveButton}</div>
			</DialogContent>
		</Dialog>
	);
};

export default DivisionListModal;
